import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import api from '../../services/api';
import { getMediaUrl } from '../../services/api';
import './WardCouncillorSection.css';

const WardCouncillorSection = ({ section, layout = 'default' }) => {
  const [councillors, setCouncillors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCouncillors();
  }, []);

  const loadCouncillors = async () => {
    try {
      const response = await api.get('/ward-councillors/', { params: { page_size: 12 } });
      const data = response.data.results || response.data;
      setCouncillors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading ward councillors:', error);
      setCouncillors([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="ward-councillor-section homepage-section">
        <Container>
          <h2 className="section-title">{section.title}</h2>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  if (councillors.length === 0) return null;

  const renderGridLayout = () => (
    <Row className="g-4">
      {councillors.map(councillor => (
        <Col key={councillor.id} lg={4} md={6} sm={12}>
          <Card className="councillor-card h-100 shadow-sm">
            {councillor.photo && (
              <div className="councillor-image-wrapper">
                <Card.Img 
                  variant="top" 
                  src={getMediaUrl(councillor.photo)} 
                  alt={councillor.name}
                  className="councillor-image"
                />
                <div className="councillor-badge">
                  <i className="fas fa-crown"></i>
                </div>
              </div>
            )}
            <Card.Body>
              <Card.Title className="councillor-name">
                {councillor.name}
              </Card.Title>
              <p className="ward-name text-info fw-bold mb-2">
                <i className="fas fa-map-marker-alt me-2"></i>
                {councillor.ward_name}
              </p>
              <p className="councillor-designation text-primary mb-2">
                {councillor.designation}
              </p>
              {councillor.constituency && (
                <p className="text-muted small mb-2">
                  <strong>Constituency:</strong> {councillor.constituency}
                </p>
              )}
              {councillor.bio && (
                  <div className="councillor-bio mt-3">
                    <div 
                      className="bio-text" 
                      dangerouslySetInnerHTML={{ __html: councillor.bio }}
                    />
                  </div>
              )}
              {councillor.email && (
                <p className="contact-info mb-1">
                  <i className="fas fa-envelope me-2 text-info"></i>
                  <a href={`mailto:${councillor.email}`}>{councillor.email}</a>
                </p>
              )}
              {councillor.phone && (
                <p className="contact-info mb-0">
                  <i className="fas fa-phone me-2 text-success"></i>
                  <a href={`tel:${councillor.phone}`}>{councillor.phone}</a>
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderCompactLayout = () => (
    <Row className="g-3">
      {councillors.map(councillor => (
        <Col key={councillor.id} lg={3} md={4} sm={6} xs={12}>
          <div className="councillor-compact-card text-center p-3 rounded border">
            {councillor.photo && (
              <div className="councillor-avatar mb-3">
                <img 
                  src={getMediaUrl(councillor.photo)} 
                  alt={councillor.name}
                  className="rounded-circle"
                />
                <div className="council-badge">
                  <i className="fas fa-crown text-warning"></i>
                </div>
              </div>
            )}
            <h5 className="councillor-name-compact mb-1">{councillor.name}</h5>
            <p className="ward-name-compact text-info small fw-bold mb-2">{councillor.ward_name}</p>
            <p className="text-primary small mb-2">{councillor.designation}</p>
            {councillor.phone && (
              <p className="text-muted small">
                <i className="fas fa-phone me-1"></i>{councillor.phone}
              </p>
            )}
            {councillor.email && (
              <p className="text-muted small">
                <a href={`mailto:${councillor.email}`} className="text-decoration-none">
                  <i className="fas fa-envelope me-1"></i>Message
                </a>
              </p>
            )}
          </div>
        </Col>
      ))}
    </Row>
  );

  const renderListLayout = () => (
    <div className="councillor-list">
      {councillors.map(councillor => (
        <div key={councillor.id} className="councillor-list-item py-4 border-bottom">
          <Row>
            {councillor.photo && (
              <Col md={2} sm={3} xs={4} className="mb-2 mb-md-0">
                <div className="position-relative">
                  <img 
                    src={getMediaUrl(councillor.photo)} 
                    alt={councillor.name}
                    className="img-fluid rounded"
                  />
                </div>
              </Col>
            )}
            <Col md={councillor.photo ? 10 : 12} sm={9} xs={8}>
              <h5 className="councillor-name">{councillor.name}</h5>
              <p className="text-info fw-bold mb-2">
                <i className="fas fa-map-marker-alt me-2"></i>
                {councillor.ward_name}
              </p>
              <p className="text-muted small mb-2">
                <strong>Position:</strong> {councillor.designation}
              </p>
              <div className="contact-links">
                {councillor.phone && (
                  <a href={`tel:${councillor.phone}`} className="btn btn-sm btn-outline-success me-2">
                    <i className="fas fa-phone me-1"></i>Call
                  </a>
                )}
                {councillor.email && (
                  <a href={`mailto:${councillor.email}`} className="btn btn-sm btn-outline-info">
                    <i className="fas fa-envelope me-1"></i>Email
                  </a>
                )}
              </div>
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );

  return (
    <section className="ward-councillor-section homepage-section">
      <Container>
        <h2 className="section-title">{section.title}</h2>
        {layout === 'compact' && renderCompactLayout()}
        {layout === 'list' && renderListLayout()}
        {layout === 'default' && renderGridLayout()}
      </Container>
    </section>
  );
};

export default WardCouncillorSection;
